import { useTranslation } from "@refinedev/core";
import { HTMLAttributes, useState } from "react";
import { FaUser, FaChevronRight } from "react-icons/fa6";
import "flag-icons/css/flag-icons.min.css";

export interface AppointmentProfessionalCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
    id: string,
    description?: string;
    name?: string;
    specialty?: string[];
    photo?: string;
    email?: string;
    active?: boolean;
    country?: {
        code: string;
        name: string;
    };
    onAction?: () => void
    contactActive?: boolean
    mobile?: boolean;
    onContinue?: () => void;
}

const AppointmentProfessionalCard = ({
    id,
    name,
    email,
    description,
    specialty,
    country,
    photo,
    className,
    active,
    onAction,
    contactActive,
    mobile,
    onContinue,
    ...rest
}: AppointmentProfessionalCardProps) => {
    const { translate } = useTranslation()
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const toggleDescription = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDescriptionExpanded(!isDescriptionExpanded);
    };

    return (
        <div
            className={`collapse bg-base-100 shadow-md hover:shadow-lg transition-all duration-200 border border-base-200 rounded-lg cursor-pointer ${active ? 'ring-2 ring-secondary collapse-open' : ''} ${className ?? ''}`}
            onClick={() => {
                if (active) return; // Already selected, don't trigger again unless it's an accordion thing
                onAction?.();
                if (!mobile) {
                    onContinue?.();
                }
            }}
            {...rest}
        >
            <input
                type="radio"
                name="professional-accordion"
                checked={active}
                onChange={() => { }}
            />
            <div className="collapse-title flex items-center gap-4">
                <div className="avatar flex-shrink-0">
                    <div className="w-16 h-16 rounded-full">
                        {photo ? (
                            <img
                                src={photo}
                                alt={name || "Professional"}
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = "/img/profile_avatar_placeholder_large.png";
                                }}
                            />
                        ) : (
                            <div className="w-full h-full bg-base-300 rounded-full flex items-center justify-center">
                                <FaUser className="w-8 h-8 text-base-content/50" />
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-base-content leading-tight mb-1 truncate">
                        {name || translate("page.helps.unknownProfessional")}
                    </h2>
                    <div className="flex flex-wrap gap-1">
                        <span className="badge badge-secondary badge-outline text-xs font-semibold py-3 truncate max-w-[120px] sm:max-w-none">
                            {translate(specialty?.[0]?.startsWith('pro-defs.')
                                ? specialty[0].replace('pro-defs.', 'pro-defs.specialties.')
                                : `pro-defs.specialties.${specialty?.[0]}`)}
                        </span>
                    </div>
                    {country && (
                        <div className={`flex items-center text-xs gap-2 mt-2 text-base-content/60 ${active ? '' : 'hidden'}`}>
                            <span className={`fi fi-${country.code.toLowerCase()} rounded-sm`}></span>
                            <span>{country.name}</span>
                        </div>
                    )}
                </div>

                <div className="flex-shrink-0 ml-2">
                    <FaChevronRight className={`w-4 h-4 text-base-content/30 transition-transform duration-200 ${active ? 'rotate-90' : ''}`} />
                </div>
            </div>

            <div className="collapse-content w-full border-t border-base-200 space-y-4">
                <div className="p-4 space-y-4">
                    {description && (
                        <div className="glass-effect p-3 rounded-lg bg-base-200/50">
                            <h3 className="font-bold text-xs uppercase tracking-wider mb-2 text-base-content/50">
                                {translate("page.helps.about")}
                            </h3>
                            <p className={`text-sm text-base-content leading-relaxed transition-all duration-300 ${!isDescriptionExpanded ? 'line-clamp-3' : ''}`}>
                                {description}
                            </p>
                            {description.length > 150 && (
                                <button
                                    onClick={toggleDescription}
                                    className="mt-2 text-primary text-xs font-bold hover:underline focus:outline-none"
                                >
                                    {isDescriptionExpanded ? translate("common.read-less") || "Ver menos" : translate("common.read-more") || "Ver más"}
                                </button>
                            )}
                        </div>
                    )}

                    {specialty && specialty.length > 1 && (
                        <div>
                            <h3 className="font-bold text-xs uppercase tracking-wider mb-2 text-base-content/50">
                                {translate("page.helps.specialties")}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {specialty.slice(1).map((s, i) => (
                                    <span key={i} className="badge badge-accent badge-outline text-xs px-3">
                                        {translate(s?.startsWith('pro-defs.')
                                            ? s.replace('pro-defs.', 'pro-defs.specialties.')
                                            : `pro-defs.specialties.${s}`)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {mobile && <button
                        className={`btn btn-primary w-full gap-2`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onContinue) onContinue();
                        }}
                        disabled={!contactActive}
                    >
                        {contactActive ? translate("common.continue") : translate("common.loading")}
                        <FaChevronRight className="w-4 h-4" />
                    </button>}
                </div>
            </div>
        </div>
    );
};

export default AppointmentProfessionalCard;
